import * as React from 'react';
// Import material ui modules.
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputAcknowledgement,
	UseInputAcknowledgementResults,
	UseInputFormResult,
	useInputText,
	UseInputTextResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormProps,
	NavigationFooterProps,
	FormDisclosuresProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
    SigningParty,
} from '@app/enumerations';
// Import api models.
import {
	AcknowledgementText,
	PossibleDisclosureText,
	Signature,
} from '@app/models/api';

interface UseFormDisclosuresParameters {
	id: string;
	props: FormDisclosuresProps,
	showValidations?: boolean;
}

interface UseFormDisclosuresResults {
	submitForm: () => void;
	acknowledgementInputs: UseInputAcknowledgementResults;
	signatureInput: UseInputTextResults
	applyFormProps: () => FormProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	isSignatureAllowed: () => boolean;
}

const useFormDisclosures = (params: UseFormDisclosuresParameters): UseFormDisclosuresResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			params.props.onSaveDisclosures(acknowledgementInputs.acknowledgements, createSignature());		
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Disclosures',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: false,
			rightButtonLabel: isSaveRequired ? 'Submit Application' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	
	const isSignatureAllowed = (): boolean => {
		return params.props.allowEdits;
	}

	// #endregion exported methods


	// #region private methods
	/**
	 * Creates a signature record. 
	 */
	const createSignature = (): Signature => {
		let signature: Signature = new Signature();
		signature.signingParty = SigningParty.PRODUCER;
		signature.signatureText = signatureInput.value;
		return signature;
	}

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return acknowledgementInputs.checkValidationWithFocus()
			&& checkInputValidationWithFocus<string>(signatureInput);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		acknowledgementInputs.changeShowValidationFlag(true);
		signatureInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return acknowledgementInputs.doAcknowledgementsHaveChanges()
			|| signatureInput.doesInputHaveChanges;
	}

	/**
	 * Searches for the matching acknowledgement text based on the possible disclosure text.
	 * @param disclosureTextId
	 * @param acknowledgements
	 */
	const findAcknowledgement = (disclosureTextId: number, acknowledgements: AcknowledgementText[]): AcknowledgementText | undefined => {
		return acknowledgements.find((acknowledgement: AcknowledgementText) => acknowledgement.textId !== null && acknowledgement.textId === disclosureTextId);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generate id for acknowledgements. 
	 **/
	const generateIdForAcknowledgements = (): string => {
		return `${generateIdForForm()}-acknowledgements`;
	}

	/**
	 * Generate id for signature. 
	 **/
	const generateIdForSignature = (): string => {
		return `${generateIdForForm()}-signature`;
	}

	//#endregion id generators

	// #region input initializers

	/**
	 * Sets up the intial acknowledgements
	 * @param possibleDisclosureTexts
	 * @param acknowledgements
	 */
	const setupInitialAcknowledgements = (possibleDisclosureTexts: PossibleDisclosureText[], acknowledgements: AcknowledgementText[]): AcknowledgementText[] => {
		return possibleDisclosureTexts.map((possibleDisclosureText: PossibleDisclosureText) => {
			let matchingAcknowledgement: AcknowledgementText | undefined = undefined;
			if (possibleDisclosureText.id !== null) {
				// Find any matching acknowledgements.
				matchingAcknowledgement = findAcknowledgement(possibleDisclosureText.id, acknowledgements);
			}

			if (matchingAcknowledgement === undefined) {
				// If there are no matching acknowledgements, then create a new one.
				matchingAcknowledgement = new AcknowledgementText();
				matchingAcknowledgement.isAcknowledged = false;
				matchingAcknowledgement.textId = possibleDisclosureText.id;
			}
			return { ...matchingAcknowledgement };
		});
	}

	// #endregion input initializers

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return params.props.allowEdits
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	// #endregion private methods


	// #region variables

	const initialAcknowledgements: AcknowledgementText[] = React.useMemo(() => {
		return setupInitialAcknowledgements(params.props.possibleDisclosureTexts, params.props.acknowledgements);
	}, [params.props.possibleDisclosureTexts, params.props.acknowledgements]);

	// Instantiate the 'acknowlecgement' inputs hooks.
	let acknowledgementInputs: UseInputAcknowledgementResults = useInputAcknowledgement({
		id: generateIdForAcknowledgements(),
		disabled: !params.props.allowEdits,
		required: true,
		acknowledgements: initialAcknowledgements,
		showValidation: params.showValidations,
	});

	// Instantiate the 'signature' input hook.
	const signatureInput: UseInputTextResults = useInputText({
		id: generateIdForSignature(),
		label: 'Signature',
		required: true,
		disabled: !params.props.allowEdits || !isSignatureAllowed(),
		value: String.empty(),
		showValidation: params.showValidations,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidationWithFocus()) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			if (params.props.possibleDisclosureTexts.length > 0
				&& params.props.possibleDisclosureTexts[0].id !== null) {
				// Focus to the first acknowledgement text input.
				acknowledgementInputs.focusToAcknowledgementInput(params.props.possibleDisclosureTexts[0].id)
			}
			else {
				signatureInput.focusToInput();
			}
		}
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		acknowledgementInputs: acknowledgementInputs,
		signatureInput: signatureInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
		isSignatureAllowed: isSignatureAllowed,
	};
}

// Export parameters.
export {
	UseFormDisclosuresParameters,
}

// Export results.
export {
	UseFormDisclosuresResults,
}

export default useFormDisclosures;